import { format } from 'date-fns'
import { z } from 'zod'

import {
  AgeVerificationMethod,
  PendingItemKind,
  PolicyComplianceErrorCode,
  ReadinessReasonCode,
  ReadinessStatus,
  SignerCapacity,
  type PolicyEquipmentScope,
} from '@hapana/shared/schemas/policy-compliance/policy-compliance-enums'
import {
  APPROVAL_PIN_PATTERN,
  PolicyEvidenceErrorCode,
} from '@hapana/shared/schemas/policy-compliance/policy-evidence-schemas'

import type { SitePermissionsKey } from '@hapana/shared/schemas/users/enums/site-permissions'
import { COMPLIANCE_SMS_SEND_WINDOW } from '@hapana/shared/constants/compliance-messaging.constants'
import { getTimePattern } from '@hapana/shared/web/utils/date-format-utils'

import { getRequestErrorCode } from '_services/graphql/request-error.utils'
import type {
  ClientCategoryReadiness,
  ClientPolicyAgreementRow,
  ClientPolicyComplianceData,
  ClientRelationRow,
  ReadinessItemRow,
} from '_services/policy-compliance.service'

export const CLIENT_POLICY_I18N = 'policyCompliance.client'

export const CLIENT_POLICY_PERMISSIONS = {
  accessClients: 'access__clients',
  viewEligibility: 'wellness__equipment_eligibility__view',
  recordPaper: 'clients__policy_forms__record_paper',
  approvePaper: 'clients__policy_forms__approve_paper',
  viewEvidence: 'clients__policy_forms__view_evidence',
  voidAcceptance: 'clients__policy_forms__void',
} as const satisfies Record<string, SitePermissionsKey>

const GUARDIAN_SUBTYPES = new Set(['PARENT', 'LEGAL_GUARDIAN'])
const FAMILY_RELATION = 'FAMILY'
const ACTIVE_RELATION = 'ACTIVE'
const POLICY_AGREEMENT = 'POLICY'
const COUNTED_AGREEMENT_STATUSES = new Set(['ACTIVE', 'COMPLETED'])

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/

const parseIsoDate = (value: string): { year: number; month: number; day: number } | null => {
  const match = ISO_DATE.exec(value)
  if (!match) {
    return null
  }
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

export const computeAgeOnDate = (
  dateOfBirth: string | null | undefined,
  onDate: string
): number | null => {
  const birthDate =
    dateOfBirth && /^\d+$/.test(dateOfBirth)
      ? new Date(Number(dateOfBirth)).toISOString()
      : dateOfBirth
  const birth = birthDate ? parseIsoDate(birthDate) : null
  const today = parseIsoDate(onDate)
  if (!birth || !today) {
    return null
  }
  const leapling = birth.month === 2 && birth.day === 29 && !isLeapYear(today.year)
  const birthdayMonth = leapling ? 3 : birth.month
  const birthdayDay = leapling ? 1 : birth.day
  const hadBirthday =
    today.month > birthdayMonth || (today.month === birthdayMonth && today.day >= birthdayDay)
  const age = today.year - birth.year - (hadBirthday ? 0 : 1)
  return age < 0 ? null : age
}

export const AgeState = {
  NOT_REQUIRED: 'NOT_REQUIRED',
  NOT_VERIFIED: 'NOT_VERIFIED',
  VERIFIED: 'VERIFIED',
} as const
export type AgeState = (typeof AgeState)[keyof typeof AgeState]

export const CategoryState = {
  READY: 'READY',
  BLOCKED: 'BLOCKED',
  PROHIBITED: 'PROHIBITED',
} as const
export type CategoryState = (typeof CategoryState)[keyof typeof CategoryState]

export type CategoryStatusRow = {
  category: PolicyEquipmentScope | null
  state: CategoryState
  blockingTitles: string[]
  needsGuardianConsent: boolean
}

export type ClientPolicyStatusSummary = {
  isEnrolled: boolean
  ageState: AgeState
  age: number | null
  guardianConsentCategories: (PolicyEquipmentScope | null)[]
  categories: CategoryStatusRow[]
  blockingCount: number
}

const isEvaluated = (category: ClientCategoryReadiness): boolean =>
  category.age.status !== ReadinessStatus.NOT_APPLICABLE ||
  category.policy.status !== ReadinessStatus.NOT_APPLICABLE

const needsGuardian = (category: ClientCategoryReadiness): boolean =>
  [...category.blockingItems, ...category.actionableItems].some(
    (item) => item.signerCapacity === SignerCapacity.GUARDIAN
  ) || category.age.reasonCodes.includes(ReadinessReasonCode.GUARDIAN_PRESENCE_REQUIRED)

const toCategoryState = (category: ClientCategoryReadiness): CategoryState => {
  if (
    category.age.reasonCodes.includes(ReadinessReasonCode.AGE_PROHIBITED) ||
    category.age.reasonCodes.includes(ReadinessReasonCode.MEDICAL_AUTHORIZATION_UNSUPPORTED)
  ) {
    return CategoryState.PROHIBITED
  }
  return category.blockingItems.length > 0 ||
    category.age.status === ReadinessStatus.FAIL ||
    category.policy.status === ReadinessStatus.FAIL ||
    category.skinType.status === ReadinessStatus.FAIL
    ? CategoryState.BLOCKED
    : CategoryState.READY
}

export const summarizeClientPolicyStatus = (
  compliance: ClientPolicyComplianceData | null | undefined,
  dateOfBirth: string | null | undefined
): ClientPolicyStatusSummary => {
  const evaluated = (compliance?.categories ?? []).filter(isEvaluated)
  const ageEvaluated = evaluated.filter(
    (category) => category.age.status !== ReadinessStatus.NOT_APPLICABLE
  )
  const notVerified = ageEvaluated.some((category) =>
    category.age.reasonCodes.includes(ReadinessReasonCode.AGE_VERIFICATION_REQUIRED)
  )
  const ageState =
    ageEvaluated.length === 0
      ? AgeState.NOT_REQUIRED
      : notVerified
        ? AgeState.NOT_VERIFIED
        : AgeState.VERIFIED
  const siteToday = compliance?.categories[0]?.evaluatedSiteLocalDate ?? null
  const categories = evaluated.map((category) => ({
    category: category.equipmentCategory,
    state: toCategoryState(category),
    blockingTitles: [...new Set(category.blockingItems.map((item) => item.title))],
    needsGuardianConsent: needsGuardian(category),
  }))
  return {
    isEnrolled: evaluated.length > 0,
    ageState,
    age: siteToday ? computeAgeOnDate(dateOfBirth, siteToday) : null,
    guardianConsentCategories:
      ageState === AgeState.VERIFIED
        ? categories.filter((row) => row.needsGuardianConsent).map((row) => row.category)
        : [],
    categories,
    blockingCount: categories.filter((row) => row.state !== CategoryState.READY).length,
  }
}

export type GuardianOption = { guardianClientId: string; name: string }

export const listGuardians = (relations: readonly ClientRelationRow[]): GuardianOption[] => {
  const seen = new Map<string, GuardianOption>()
  for (const relation of relations) {
    if (
      relation.type === FAMILY_RELATION &&
      relation.status === ACTIVE_RELATION &&
      GUARDIAN_SUBTYPES.has(relation.relationshipSubtype ?? '')
    ) {
      seen.set(relation.counterpartyClientId, {
        guardianClientId: relation.counterpartyClientId,
        name: relation.name,
      })
    }
  }
  return [...seen.values()].sort((left, right) => left.name.localeCompare(right.name))
}

export const listVoidableAcceptances = (
  agreements: readonly ClientPolicyAgreementRow[]
): ClientPolicyAgreementRow[] =>
  agreements.filter(
    (agreement) =>
      agreement.type === POLICY_AGREEMENT &&
      COUNTED_AGREEMENT_STATUSES.has(agreement.status) &&
      agreement.signedAt !== null
  )

export type PaperSlotCandidate = {
  slotKey: string
  title: string
  sitePolicyId: string
  policyRevisionId: string
  signerCapacity: SignerCapacity
  signerId: string | null
}

const toSlotCapacity = (item: ReadinessItemRow): SignerCapacity =>
  item.signerCapacity ?? SignerCapacity.CLIENT_ADULT

export const buildPaperSlotCandidates = (
  compliance: ClientPolicyComplianceData | null | undefined
): PaperSlotCandidate[] => {
  const slots = new Map<string, PaperSlotCandidate>()
  for (const category of compliance?.categories ?? []) {
    if (toCategoryState(category) === CategoryState.PROHIBITED) {
      continue
    }
    for (const item of [...category.blockingItems, ...category.actionableItems]) {
      if (
        item.kind !== PendingItemKind.SITE_POLICY ||
        !item.sitePolicyId ||
        !item.policyRevisionId
      ) {
        continue
      }
      const capacity = toSlotCapacity(item)
      const slotKey = `${item.sitePolicyId}:${capacity}`
      if (!slots.has(slotKey)) {
        slots.set(slotKey, {
          slotKey,
          title: item.title,
          sitePolicyId: item.sitePolicyId,
          policyRevisionId: item.policyRevisionId,
          signerCapacity: capacity,
          signerId: capacity === SignerCapacity.GUARDIAN ? null : item.signerId,
        })
      }
    }
  }
  return [...slots.values()].sort((left, right) => left.title.localeCompare(right.title))
}

export const PaperFormError = {
  SLOT_REQUIRED: 'SLOT_REQUIRED',
  SIGNER_REQUIRED: 'SIGNER_REQUIRED',
  DATE_REQUIRED: 'DATE_REQUIRED',
  DATE_IN_FUTURE: 'DATE_IN_FUTURE',
  REVISION_REQUIRED: 'REVISION_REQUIRED',
  SCAN_REQUIRED: 'SCAN_REQUIRED',
  SCAN_TOO_LARGE: 'SCAN_TOO_LARGE',
  SCAN_WRONG_TYPE: 'SCAN_WRONG_TYPE',
  APPROVER_REQUIRED: 'APPROVER_REQUIRED',
  SELF_APPROVAL: 'SELF_APPROVAL',
  PIN_INVALID: 'PIN_INVALID',
  ATTESTATION_REQUIRED: 'ATTESTATION_REQUIRED',
} as const

export const PAPER_SCAN_MAX_BYTES = 15 * 1024 * 1024
export const PAPER_SCAN_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const

const paperSlotSchema = z.object({
  slotKey: z.string(),
  selected: z.boolean(),
  sitePolicyId: z.string(),
  signerCapacity: z.enum(SignerCapacity),
  policyRevisionId: z.string(),
  signerId: z.string(),
  signedOn: z.string(),
})

export const pickScanFile = (value: FileList | File | null | undefined): File | null => {
  if (!value) {
    return null
  }
  if ('item' in value && typeof value.item === 'function') {
    return value.item(0)
  }
  return value as File
}

export const buildPaperDetailsSchema = (siteToday: string) =>
  z
    .object({
      slots: z.array(paperSlotSchema),
      scanFile: z.custom<FileList | File | null>(),
    })
    .superRefine((values, ctx) => {
      const selected = values.slots
        .map((slot, index) => ({ slot, index }))
        .filter(({ slot }) => slot.selected)
      if (selected.length === 0) {
        ctx.addIssue({ code: 'custom', message: PaperFormError.SLOT_REQUIRED, path: ['slots'] })
      }
      for (const { slot, index } of selected) {
        if (!slot.policyRevisionId) {
          ctx.addIssue({
            code: 'custom',
            message: PaperFormError.REVISION_REQUIRED,
            path: ['slots', index, 'policyRevisionId'],
          })
        }
        if (!slot.signerId) {
          ctx.addIssue({
            code: 'custom',
            message: PaperFormError.SIGNER_REQUIRED,
            path: ['slots', index, 'signerId'],
          })
        }
        const signedOn = slot.signedOn.slice(0, 10)
        if (!signedOn) {
          ctx.addIssue({
            code: 'custom',
            message: PaperFormError.DATE_REQUIRED,
            path: ['slots', index, 'signedOn'],
          })
        } else if (signedOn > siteToday) {
          ctx.addIssue({
            code: 'custom',
            message: PaperFormError.DATE_IN_FUTURE,
            path: ['slots', index, 'signedOn'],
          })
        }
      }
      const file = pickScanFile(values.scanFile)
      if (!file) {
        ctx.addIssue({ code: 'custom', message: PaperFormError.SCAN_REQUIRED, path: ['scanFile'] })
      } else if (!PAPER_SCAN_TYPES.includes(file.type as (typeof PAPER_SCAN_TYPES)[number])) {
        ctx.addIssue({
          code: 'custom',
          message: PaperFormError.SCAN_WRONG_TYPE,
          path: ['scanFile'],
        })
      } else if (file.size > PAPER_SCAN_MAX_BYTES) {
        ctx.addIssue({ code: 'custom', message: PaperFormError.SCAN_TOO_LARGE, path: ['scanFile'] })
      }
    })

export type PaperDetailsValues = z.infer<ReturnType<typeof buildPaperDetailsSchema>>

export const buildPaperApprovalSchema = (currentStaffUserId: string | null) =>
  z
    .object({
      approverStaffUserId: z.string().min(1, PaperFormError.APPROVER_REQUIRED),
      approverPin: z.string().regex(APPROVAL_PIN_PATTERN, PaperFormError.PIN_INVALID),
      attested: z.boolean().refine(Boolean, { message: PaperFormError.ATTESTATION_REQUIRED }),
    })
    .superRefine((values, ctx) => {
      if (isSelfApproval(values.approverStaffUserId, currentStaffUserId)) {
        ctx.addIssue({
          code: 'custom',
          message: PaperFormError.SELF_APPROVAL,
          path: ['approverStaffUserId'],
        })
      }
    })

export type PaperApprovalValues = z.infer<ReturnType<typeof buildPaperApprovalSchema>>

export const isSelfApproval = (
  approverStaffUserId: string | null | undefined,
  currentStaffUserId: string | null | undefined
): boolean =>
  Boolean(approverStaffUserId) &&
  Boolean(currentStaffUserId) &&
  approverStaffUserId === currentStaffUserId

export const toPaperSlotsInput = (
  values: PaperDetailsValues,
  signerNames: Record<string, string>
) =>
  values.slots
    .filter((slot) => slot.selected)
    .map((slot) => ({
      policyRevisionId: slot.policyRevisionId,
      signerCapacity: slot.signerCapacity,
      signerId: slot.signerId,
      signerName: signerNames[slot.signerId] ?? '',
      signedOn: slot.signedOn.slice(0, 10),
    }))

const POLICY_ERROR_COPY: Record<string, string> = {
  [PolicyEvidenceErrorCode.SELF_APPROVAL_REFUSED]: 'selfApproval',
  [PolicyEvidenceErrorCode.APPROVER_NOT_AUTHORISED]: 'approverNotAuthorised',
  [PolicyEvidenceErrorCode.APPROVER_PIN_NOT_SET]: 'approverPinNotSet',
  [PolicyEvidenceErrorCode.APPROVER_PIN_INVALID]: 'approverPinInvalid',
  [PolicyEvidenceErrorCode.APPROVER_PIN_LOCKED]: 'approverPinLocked',
  [PolicyEvidenceErrorCode.CURRENT_PIN_REQUIRED]: 'currentPinRequired',
  [PolicyEvidenceErrorCode.RECENT_LOGIN_REQUIRED]: 'recentLoginRequired',
  [PolicyEvidenceErrorCode.CLIENT_NOT_AT_SITE]: 'clientNotAtSite',
  [PolicyEvidenceErrorCode.REVISION_NOT_FOUND]: 'revisionNotFound',
  [PolicyEvidenceErrorCode.SIGNED_ON_IN_FUTURE]: 'signedOnInFuture',
  [PolicyEvidenceErrorCode.SIGNER_CAPACITY_INVALID]: 'signerCapacityInvalid',
  [PolicyEvidenceErrorCode.SIGNER_NOT_FOUND]: 'signerNotFound',
  [PolicyEvidenceErrorCode.SCAN_INVALID]: 'scanInvalid',
  [PolicyComplianceErrorCode.CAPACITY_MISMATCH]: 'capacityMismatch',
  [PolicyComplianceErrorCode.ELIGIBILITY_UNAVAILABLE]: 'eligibilityUnavailable',
}

export const getPolicyErrorCopyKey = (error: unknown): string | null => {
  const code = getRequestErrorCode(error)
  const leaf = code ? POLICY_ERROR_COPY[code] : undefined
  return leaf ? `${CLIENT_POLICY_I18N}.errors.${leaf}` : null
}

export const isSelfApprovalError = (error: unknown): boolean =>
  getRequestErrorCode(error) === PolicyEvidenceErrorCode.SELF_APPROVAL_REFUSED

export const WithdrawAction = {
  WITHDRAW_CONSENT: 'WITHDRAW_CONSENT',
  WITHDRAW_GUARDIAN_PERMISSION: 'WITHDRAW_GUARDIAN_PERMISSION',
  VOID_ACCEPTANCE: 'VOID_ACCEPTANCE',
  REVOKE_GUARDIAN_AUTHORITY: 'REVOKE_GUARDIAN_AUTHORITY',
} as const
export type WithdrawAction = (typeof WithdrawAction)[keyof typeof WithdrawAction]

export const WithdrawFormError = {
  SIGNER_REQUIRED: 'SIGNER_REQUIRED',
  GUARDIAN_REQUIRED: 'GUARDIAN_REQUIRED',
  AGREEMENT_REQUIRED: 'AGREEMENT_REQUIRED',
  REASON_REQUIRED: 'REASON_REQUIRED',
} as const

export const listAvailableWithdrawActions = (options: {
  hasGuardians: boolean
  canAccessClients: boolean
  canVoid: boolean
}): WithdrawAction[] =>
  [
    options.canAccessClients ? WithdrawAction.WITHDRAW_CONSENT : null,
    options.canAccessClients && options.hasGuardians
      ? WithdrawAction.WITHDRAW_GUARDIAN_PERMISSION
      : null,
    options.canVoid ? WithdrawAction.VOID_ACCEPTANCE : null,
    options.canAccessClients && options.hasGuardians
      ? WithdrawAction.REVOKE_GUARDIAN_AUTHORITY
      : null,
  ].filter((action): action is WithdrawAction => action !== null)

export const withdrawFormSchema = z
  .object({
    action: z.enum(WithdrawAction),
    signerId: z.string(),
    guardianClientId: z.string(),
    agreementId: z.string(),
    reason: z.string().max(500),
  })
  .superRefine((values, ctx) => {
    if (values.action === WithdrawAction.WITHDRAW_CONSENT && !values.signerId) {
      ctx.addIssue({
        code: 'custom',
        message: WithdrawFormError.SIGNER_REQUIRED,
        path: ['signerId'],
      })
    }
    if (
      (values.action === WithdrawAction.WITHDRAW_GUARDIAN_PERMISSION ||
        values.action === WithdrawAction.REVOKE_GUARDIAN_AUTHORITY) &&
      !values.guardianClientId
    ) {
      ctx.addIssue({
        code: 'custom',
        message: WithdrawFormError.GUARDIAN_REQUIRED,
        path: ['guardianClientId'],
      })
    }
    if (values.action === WithdrawAction.VOID_ACCEPTANCE) {
      if (!values.agreementId) {
        ctx.addIssue({
          code: 'custom',
          message: WithdrawFormError.AGREEMENT_REQUIRED,
          path: ['agreementId'],
        })
      }
      if (values.reason.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: WithdrawFormError.REASON_REQUIRED,
          path: ['reason'],
        })
      }
    }
  })

export type WithdrawFormValues = z.infer<typeof withdrawFormSchema>

export const VerifyAgeFormError = {
  DOB_REQUIRED: 'DOB_REQUIRED',
  DOB_IN_FUTURE: 'DOB_IN_FUTURE',
  ATTESTATION_REQUIRED: 'ATTESTATION_REQUIRED',
} as const

export const verifyAgeFormSchema = (siteToday: string) =>
  z
    .object({
      dateOfBirth: z
        .string()
        .min(1, VerifyAgeFormError.DOB_REQUIRED)
        .refine((value) => value.slice(0, 10) <= siteToday, {
          message: VerifyAgeFormError.DOB_IN_FUTURE,
        }),
      method: z.enum(AgeVerificationMethod),
      attested: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (values.method === AgeVerificationMethod.STAFF_ID_CHECK && !values.attested) {
        ctx.addIssue({
          code: 'custom',
          message: VerifyAgeFormError.ATTESTATION_REQUIRED,
          path: ['attested'],
        })
      }
    })

export type VerifyAgeFormValues = z.infer<ReturnType<typeof verifyAgeFormSchema>>

export const isValidTimezone = (value: string | null | undefined): value is string => {
  if (!value) {
    return false
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone: value })
    return true
  } catch {
    return false
  }
}

export const resolvePolicySiteTimezone = (
  contextTimezone: string | null | undefined,
  siteTimezone: string | null | undefined
): string | null => [contextTimezone, siteTimezone].find(isValidTimezone) ?? null

export const formatPersonName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string => [firstName, lastName].filter(Boolean).join(' ')

export const listPaperApproverOptions = (
  approvers: readonly { staffUserId: string; firstName: string | null; lastName: string | null }[],
  currentStaffUserId: string | null
): { value: string; label: string }[] =>
  approvers
    .filter((approver) => approver.staffUserId !== currentStaffUserId)
    .map((approver) => ({
      value: approver.staffUserId,
      label: formatPersonName(approver.firstName, approver.lastName),
    }))

export const formatPolicyList = (labels: readonly string[], locale = 'en'): string =>
  new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(labels)

export const ApprovalPinFormError = {
  PIN_INVALID: 'PIN_INVALID',
  PIN_MISMATCH: 'PIN_MISMATCH',
  CURRENT_PIN_REQUIRED: 'CURRENT_PIN_REQUIRED',
  PASSWORD_REQUIRED: 'PASSWORD_REQUIRED',
} as const

export const isRecentLoginRequired = (error: unknown): boolean =>
  getRequestErrorCode(error) === PolicyEvidenceErrorCode.RECENT_LOGIN_REQUIRED

export const buildApprovalPinFormSchema = (hasPin: boolean, needsPassword = false) =>
  z
    .object({
      currentPin: z.string(),
      pin: z.string().regex(APPROVAL_PIN_PATTERN, ApprovalPinFormError.PIN_INVALID),
      confirmPin: z.string(),
      password: z.string(),
    })
    .superRefine((values, ctx) => {
      if (values.pin !== values.confirmPin) {
        ctx.addIssue({
          code: 'custom',
          message: ApprovalPinFormError.PIN_MISMATCH,
          path: ['confirmPin'],
        })
      }
      if (hasPin && !APPROVAL_PIN_PATTERN.test(values.currentPin)) {
        ctx.addIssue({
          code: 'custom',
          message: ApprovalPinFormError.CURRENT_PIN_REQUIRED,
          path: ['currentPin'],
        })
      }
      if (needsPassword && !values.password) {
        ctx.addIssue({
          code: 'custom',
          message: ApprovalPinFormError.PASSWORD_REQUIRED,
          path: ['password'],
        })
      }
    })

export type ApprovalPinFormValues = z.infer<ReturnType<typeof buildApprovalPinFormSchema>>

const formatClockHour = (hour: number): string =>
  format(new Date(2000, 0, 1, hour), getTimePattern())

export const formatComplianceSmsSendWindow = (): { start: string; end: string } => ({
  start: formatClockHour(COMPLIANCE_SMS_SEND_WINDOW.startHour),
  end: formatClockHour(COMPLIANCE_SMS_SEND_WINDOW.endHour),
})
